/**
 * Halloween emotes — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenEmotesPack: ToilePack = {
    slug: 'halloween-emotes',
    category: 'season',
    name: 'Halloween emotes',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'scream', label: 'Screaming pumpkin', subject: 'A round pumpkin face with wide eyes and an open screaming mouth, built to read tiny, isolated'},
        {key: 'shy', label: 'Shy ghost', subject: 'A round ghost face peeking with blushing cheeks and one hand up, built to read tiny, isolated'},
        {key: 'vampire', label: 'Vampire grin', subject: 'A round pale face with a wide grin showing two fangs, built to read tiny, isolated'},
    ],
    styles: [
        {key: 'cute', label: 'Spooky cute', style: 'kawaii spooky illustration, rounded shapes, soft outlines, friendly faces', palette: 'Pastel purple, orange, mint and cream with black accents', framing: 'Centered on a solid background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: '1950s halloween paper decoration style, halftone grain, slight misregistration', palette: 'Orange, black, cream and a faded green', framing: 'Centered on an aged cream paper background with an even margin'},
        {key: 'bold', label: 'Bold graphic', style: 'bold graphic illustration, thick outlines, high contrast, flat fills', palette: 'Orange, black and white with a single purple', framing: 'Centered on a solid background with a little room around it'},
    ],
    directions: ['laughing instead', 'as a heart-eyes version', 'for a Discord server'],
    directionHint: 'try: a laughing version instead',
    seo: {
        title: 'Halloween Emotes: Pumpkin, Ghost & Vampire Faces',
        metaDescription: 'Nine Halloween emotes — screaming pumpkin, shy ghost, vampire grin — in spooky-cute, vintage, and bold graphic styles, built to read at 28 pixels.',
        h1: 'Halloween emotes: screaming pumpkin, shy ghost, vampire grin',
        tagline: 'Nine emotes, three expressions, three moods each.',
        intro: 'Nine chat emotes for October: a screaming pumpkin, a blushing shy ghost, and a fanged vampire grin, each drawn in a spooky-cute, a vintage 1950s paper, and a bold graphic register. Every face is built to hold its expression at the tiny sizes a Twitch or Discord emote actually renders at.',
        useCases: [
            "a Twitch channel's Halloween emote set",
            "a Discord server's seasonal emoji pack",
            'a sticker reaction for chat and DMs',
            'a small badge on a stream overlay',
        ],
        faq: [
            {q: 'What size do the emotes work at?', a: 'Each design is built and checked to stay readable down to the roughly 28-pixel size Twitch and Discord render emotes at, not just as a large graphic.'},
            {q: 'How many emotes do I get?', a: 'Nine files: three expressions — pumpkin, ghost, vampire — each in three visual styles, so you can pick the register that matches your channel.'},
            {q: 'Can I use these on Twitch and Discord both?', a: 'Yes — the files are plain transparent-background images you upload wherever the platform accepts custom emotes or emoji.'},
            {q: 'Is there text or a watermark on the emotes?', a: 'No — every emote is the face alone with a clean background, ready to upload as-is.'},
        ],
    },
}
